export function clampPercent(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return 0;
  }
  return Math.min(100, Math.max(0, number));
}

export function formatDateTime(value, locale) {
  if (!value) {
    return '—';
  }
  try {
    return new Intl.DateTimeFormat(locale === 'ar' ? 'ar' : 'en', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(value));
  } catch {
    return String(value);
  }
}

export function formatDate(value, locale) {
  if (!value) {
    return '—';
  }
  try {
    return new Intl.DateTimeFormat(locale === 'ar' ? 'ar' : 'en', {
      dateStyle: 'medium',
    }).format(new Date(value));
  } catch {
    return String(value);
  }
}

export function formatNumber(value, locale) {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return '—';
  }
  return new Intl.NumberFormat(locale === 'ar' ? 'ar' : 'en').format(number);
}

export function maskPhone(value) {
  const phone = String(value || '');
  if (!phone) {
    return '—';
  }
  if (phone.includes('*') || phone.includes('•')) {
    return phone;
  }
  if (phone.length <= 6) {
    return phone;
  }
  return `${phone.slice(0, 4)}••••${phone.slice(-3)}`;
}

export function localizedText(entity, locale, keys = ['name', 'title', 'text', 'label', 'body']) {
  if (!entity) {
    return '';
  }
  if (typeof entity === 'string') {
    return entity;
  }
  const suffix = locale === 'ar' ? 'ar' : 'en';
  for (const key of keys) {
    const localized = entity[`${key}_${suffix}`] || entity[key];
    if (localized) {
      return localized;
    }
  }
  return '';
}

export function isLocalDevelopment() {
  return process.env.NODE_ENV !== 'production';
}

export function isValidTimeZone(value) {
  if (!value || typeof value !== 'string') {
    return false;
  }
  try {
    Intl.DateTimeFormat('en', { timeZone: value }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

export function formatDateTimeInTimeZone(value, locale, timeZone) {
  if (!value) {
    return { datetime: '—', timeZoneLabel: '' };
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return { datetime: String(value), timeZoneLabel: '' };
  }
  const loc = locale === 'ar' ? 'ar' : 'en';
  const zone = isValidTimeZone(timeZone) ? timeZone : null;
  try {
    const datetime = new Intl.DateTimeFormat(loc, {
      dateStyle: 'medium',
      timeStyle: 'short',
      ...(zone ? { timeZone: zone } : {}),
    }).format(date);
    if (!zone) {
      return { datetime, timeZoneLabel: '' };
    }
    const zonePart = new Intl.DateTimeFormat(loc, {
      timeZone: zone,
      timeZoneName: 'short',
    }).formatToParts(date).find((part) => part.type === 'timeZoneName');
    return { datetime, timeZoneLabel: zonePart?.value || zone };
  } catch {
    return { datetime: String(value), timeZoneLabel: '' };
  }
}

export function safeAbsoluteHttpUrl(value, { allowHttp = isLocalDevelopment() } = {}) {
  if (!value || typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed || trimmed.includes('\\') || /\s/.test(trimmed)) {
    return null;
  }
  if (!/^https?:\/\//i.test(trimmed)) {
    return null;
  }
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol === 'https:') {
      return parsed.toString();
    }
    if (parsed.protocol === 'http:' && allowHttp) {
      return parsed.toString();
    }
    return null;
  } catch {
    return null;
  }
}

export function safeHttpUrl(value) {
  return safeAbsoluteHttpUrl(value, { allowHttp: true });
}

export function safeAvatarUrl(value) {
  return safeAbsoluteHttpUrl(value);
}

export function safeMeetingUrl(value, status) {
  if (status !== 'scheduled') {
    return null;
  }
  return safeAbsoluteHttpUrl(value);
}

export function asList(data) {
  if (Array.isArray(data)) {
    return data;
  }
  if (Array.isArray(data?.items)) {
    return data.items;
  }
  if (Array.isArray(data?.data)) {
    return data.data;
  }
  return [];
}

export function flattenQuestions(assessment) {
  const sections = Array.isArray(assessment?.sections) ? assessment.sections : [];
  return sections.flatMap((section, sectionIndex) => (
    (Array.isArray(section.questions) ? section.questions : []).map((question, questionIndex) => ({
      ...question,
      section,
      sectionIndex,
      questionIndex,
    }))
  ));
}

export function firstUnansweredIndex(questions, selectedAnswers = []) {
  const answered = new Set(
    (selectedAnswers || [])
      .filter((row) => row?.question_id != null && row?.answer_id != null)
      .map((row) => Number(row.question_id)),
  );
  const index = questions.findIndex((question) => !answered.has(Number(question.id)));
  return index < 0 ? Math.max(0, questions.length - 1) : index;
}

export function answersMap(selectedAnswers = []) {
  return (selectedAnswers || []).reduce((map, row) => {
    if (row?.question_id != null && row?.answer_id != null) {
      map[Number(row.question_id)] = Number(row.answer_id);
    }
    return map;
  }, {});
}

export function omitScoringFields(payload) {
  if (!payload || typeof payload !== 'object') {
    return payload;
  }
  const scoringKeys = ['raw_score', 'min_score', 'max_score', 'weights', 'weight_matrix', 'fingerprint', 'bounds', 'scoring_matrix'];
  const stripObject = (value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return value;
    }
    const next = { ...value };
    scoringKeys.forEach((key) => {
      delete next[key];
    });
    return next;
  };
  const next = stripObject(payload);
  ['patterns', 'top_patterns'].forEach((key) => {
    if (Array.isArray(next[key])) {
      next[key] = next[key].map(stripObject);
    }
  });
  return next;
}
